import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, unwrap } from '../../../api/client';
import { errorMessage } from '../../../api/errors';
import type { components } from '../../../api/schema';
import { Badge, Card, Input, Pagination, Table } from '../../../design';
import { fmtDateTime } from '../../../lib/format';
import { queryParams, useUrlState } from '../../../lib/urlState';
import type { LockerDetail } from '../common';

type DeviceEvent = components['schemas']['DeviceEventRecord'];
type ConfigVersion = components['schemas']['ConfigVersion'];

export function LogTab({ locker: l }: { locker: LockerDetail }) {
  const { values, page, pageSize, set } = useUrlState(['type'] as const);
  const [expanded, setExpanded] = useState<string | null>(null);
  const events = useQuery({
    queryKey: ['locker', l.id, 'events', { type: values.type, page, pageSize }],
    queryFn: () =>
      unwrap(
        api.GET('/lockers/{id}/events', {
          params: { path: { id: l.id }, query: queryParams({ type: values.type, page, pageSize }) },
        }),
      ),
  });
  const versions = useQuery({
    queryKey: ['locker', l.id, 'versions'],
    queryFn: () =>
      unwrap(
        api.GET('/lockers/{id}/config/versions', {
          params: { path: { id: l.id }, query: { pageSize: 10 } },
        }),
      ),
  });
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 16 }}>
      <Card tight>
        <div className="flex" style={{ padding: 16 }}>
          <h2>Evenimente de la dispozitiv</h2>
          <Input
            placeholder="Tip (ex. door.opened)…"
            value={values.type}
            onChange={(e) => set({ type: e.target.value })}
            style={{ maxWidth: 220, marginLeft: 'auto' }}
            aria-label="Tip eveniment"
          />
        </div>
        <Table<DeviceEvent>
          columns={[
            {
              key: 'ts',
              header: 'Moment (dispozitiv)',
              render: (e) => <span className="nowrap">{fmtDateTime(e.ts)}</span>,
            },
            { key: 'type', header: 'Tip', render: (e) => <span className="mono">{e.type}</span> },
            {
              key: 'source',
              header: 'Sursă',
              render: (e) => (
                <Badge tone={e.source === 'mqtt' ? 'blue' : 'neutral'}>{e.source}</Badge>
              ),
            },
            {
              key: 'payload',
              header: 'Conținut',
              render: (e) => (
                <span className="mono small muted" style={{ wordBreak: 'break-all' }}>
                  {expanded === e.id
                    ? JSON.stringify(e.payload)
                    : JSON.stringify(e.payload).slice(0, 60) +
                      (JSON.stringify(e.payload).length > 60 ? '…' : '')}
                </span>
              ),
            },
          ]}
          rows={events.data?.items ?? []}
          rowKey={(e) => e.id}
          onRowClick={(e) => setExpanded(expanded === e.id ? null : e.id)}
          empty={
            events.isLoading
              ? 'Se încarcă…'
              : events.error
                ? errorMessage(events.error)
                : 'Niciun eveniment.'
          }
          footer={
            events.data && (
              <Pagination
                page={events.data.page}
                pageSize={events.data.pageSize}
                total={events.data.total}
                onPage={(p) => set({ page: p })}
              />
            )
          }
        />
      </Card>
      <Card tight>
        <div style={{ padding: 16 }}>
          <h2>Versiuni de configurație</h2>
        </div>
        <Table<ConfigVersion>
          columns={[
            { key: 'v', header: 'Versiune', render: (v) => <strong>v{v.version}</strong> },
            {
              key: 'pub',
              header: 'Publicată',
              render: (v) => <span className="small">{fmtDateTime(v.publishedAt)}</span>,
            },
            {
              key: 'state',
              header: 'Stare',
              render: (v) =>
                v.failedAt ? (
                  <Badge tone="red">eșuată</Badge>
                ) : v.appliedAt ? (
                  <Badge tone="green">aplicată</Badge>
                ) : v.publishedAt ? (
                  <Badge tone="orange">în așteptare</Badge>
                ) : (
                  <Badge tone="neutral">draft</Badge>
                ),
            },
            {
              key: 'by',
              header: 'De',
              render: (v) => <span className="small muted">{v.createdBy}</span>,
            },
          ]}
          rows={versions.data?.items ?? []}
          rowKey={(v) => String(v.version)}
          empty={
            versions.isLoading
              ? 'Se încarcă…'
              : versions.error
                ? errorMessage(versions.error)
                : 'Nicio versiune publicată încă.'
          }
        />
        {versions.data?.items.some((v) => v.failureReason) && (
          <p className="small" style={{ padding: 16, color: 'var(--red)' }}>
            {versions.data.items.find((v) => v.failureReason)?.failureReason}
          </p>
        )}
      </Card>
    </div>
  );
}
